import React from "react";
import { MapPin, Search, X } from "lucide-react";
import { useIntl } from "react-intl";
import useEscapeKey from "../../hooks/useEscapeKey";
import useTimezoneSearch from "../../hooks/useTimezoneSearch";
import { formatTimezoneName, formatUtcOffset, getBrowserTimezone } from "../../timezones";
import TimezoneList from "./TimezoneList";
import "./TimezonePanel.sass";

const ICON_SIZE = 18;
const SMALL_ICON_SIZE = 16;

/**
 * Side panel of the profile time zone (po-chart-visual-spec.md §10), opened
 * by ⚙ next to the chart clock: «Your location» picks the browser zone, the
 * list with search picks any zone. Closes on ✕, Esc and a click outside.
 * Rendered only while open, so the search starts empty every time.
 *
 * @param {object} props
 * @param {string} props.timezone - current zone of the profile
 * @param {boolean} props.isSaving - the zone is being saved to the profile
 * @param {(timezone: string) => void} props.onSelect
 * @param {() => void} props.onClose
 */
const TimezonePanel = ({ timezone, isSaving, onSelect, onClose }) => {
  const intl = useIntl();
  const t = (id) => intl.formatMessage({ id });
  const { query, setQuery, options } = useTimezoneSearch(timezone);
  const browserTimezone = getBrowserTimezone();
  const isLocationActive = browserTimezone === timezone;

  useEscapeKey(onClose);

  const handleBackdropClick = (event) => {
    if (event.target === event.currentTarget) onClose();
  };

  return (
    <div className="timezone-panel__backdrop" onClick={handleBackdropClick}>
      <aside
        className="timezone-panel"
        role="dialog"
        aria-modal="true"
        aria-label={t("futures.chart.timezone.title")}
      >
        <div className="timezone-panel__header">
          <h2 className="timezone-panel__title">{t("futures.chart.timezone.title")}</h2>
          <button
            type="button"
            className="timezone-panel__close"
            aria-label={t("futures.chart.timezone.close")}
            onClick={onClose}
          >
            <X size={ICON_SIZE} aria-hidden="true" />
          </button>
        </div>

        <p className="timezone-panel__description">{t("futures.chart.timezone.description")}</p>

        <button
          type="button"
          className={`timezone-panel__location${isLocationActive ? " timezone-panel__location--active" : ""}`}
          aria-pressed={isLocationActive}
          disabled={isSaving}
          onClick={() => onSelect(browserTimezone)}
        >
          <MapPin size={SMALL_ICON_SIZE} aria-hidden="true" />
          <span className="timezone-panel__location-text">
            {t("futures.chart.timezone.your_location")}: {formatTimezoneName(browserTimezone)}{" "}
            {formatUtcOffset(browserTimezone)}
          </span>
        </button>

        <label className="timezone-panel__search">
          <Search size={SMALL_ICON_SIZE} aria-hidden="true" />
          <input
            type="search"
            className="timezone-panel__search-input"
            placeholder={t("futures.chart.timezone.search")}
            aria-label={t("futures.chart.timezone.search")}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
        </label>

        <TimezoneList
          options={options}
          timezone={timezone}
          isDisabled={isSaving}
          onSelect={onSelect}
          emptyText={t("futures.chart.timezone.not_found")}
        />
      </aside>
    </div>
  );
};

export default TimezonePanel;
